import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import { useOnlineStatus } from '../../../hooks/useOnlineStatus';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ModifierRendezVousModal from '../../../components/feature/ModifierRendezVousModal';
import CalendarWeekView from './components/CalendarWeekView';

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
  email: string;
  telephone: string;
}

interface Disponibilite {
  id: string;
  jour_semaine: number;
  heure_debut: string;
  heure_fin: string;
  actif: boolean;
}

const JOURS_SEMAINE = [
  'Dimanche',
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi'
];

interface RendezVous {
  id: string;
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  statut: string;
  patient_id: string;
  medecin_id: string;
  type_rdv?: string;
  patients: {
    nom: string;
    prenom: string;
    telephone?: string;
    email?: string;
  } | null;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  date_naissance?: string;
  adresse?: string;
  created_at: string;
}

interface AgendaSlot {
  heure_debut: string;
  heure_fin: string;
  est_occupe: boolean;
  rdv_info: string | null;
}

interface AgendaMedecin {
  medecin: Medecin;
  disponibilites: Disponibilite[];
  slots: AgendaSlot[];
}

export default function SecretaireDashboardPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [medecins, setMedecins] = useState<Medecin[]>([]);
  const [selectedMedecin, setSelectedMedecin] = useState<Medecin | null>(null);
  const [disponibilites, setDisponibilites] = useState<Disponibilite[]>([]);
  const [toutesDisponibilites, setToutesDisponibilites] = useState<Disponibilite[]>([]);
  const [rendezVous, setRendezVous] = useState<RendezVous[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'medecins' | 'rendez-vous' | 'disponibilites' | 'agenda'>('medecins');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRdvToEdit, setSelectedRdvToEdit] = useState<RendezVous | null>(null);
  const [showModifierModal, setShowModifierModal] = useState(false);
  const { mutate: offlineMutate } = useOfflineMutation<any>();
  const { isOnline: hookIsOnline } = useOnlineStatus();

  const [agendaDate, setAgendaDate] = useState(new Date().toISOString().split('T')[0]);
  const [agendaMedecinFilter, setAgendaMedecinFilter] = useState('');
  const [agendaData, setAgendaData] = useState<AgendaMedecin[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(false);

  // ✅ useEffect TOUJOURS appelé en premier
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (role !== 'secretaire' && role !== 'super_admin') {
      navigate('/', { replace: true });
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        await loadData();
      } finally {
        setLoading(false);
      }
    };

    run();

    // ✅ Écouter les changements en temps réel sur les disponibilités
    const disponibilitesChannel = supabase
      .channel('disponibilites_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'disponibilites_medecins'
        },
        async (payload) => {
          console.log('🔄 Changement détecté sur les disponibilités:', payload);
          await loadToutesDisponibilites();
          if (selectedMedecin) {
            await loadDisponibilites(selectedMedecin.id);
          }
        }
      )
      .subscribe();

    // ✅ Écouter les changements en temps réel sur les rendez-vous
    const rendezVousChannel = supabase
      .channel('rendez_vous_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rendez_vous'
        },
        async (payload) => {
          console.log('🔄 Changement détecté sur les rendez-vous:', payload);
          await loadRendezVous();
        }
      )
      .subscribe();

    // ✅ Écouter les changements en temps réel sur les patients
    const patientsChannel = supabase
      .channel('patients_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patients'
        },
        async (payload) => {
          console.log('🔄 Changement détecté sur les patients:', payload);
          await loadPatients();
        }
      )
      .subscribe();

    // ✅ Nettoyage des subscriptions
    return () => {
      supabase.removeChannel(disponibilitesChannel);
      supabase.removeChannel(rendezVousChannel);
      supabase.removeChannel(patientsChannel);
    };
  }, [user, role, authLoading, navigate, selectedMedecin]);

  useEffect(() => {
    if (activeTab === 'agenda') {
      loadAgendaData();
    }
  }, [activeTab, agendaDate, agendaMedecinFilter, rendezVous, toutesDisponibilites]);

  // ✅ return conditionnel APRÈS tous les hooks
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  const loadData = async () => {
    try {
      await Promise.all([
        loadMedecins(),
        loadRendezVous(),
        loadToutesDisponibilites()
      ]);
    } catch (error) {
      // Silencieux
    }
  };

  const loadMedecins = async () => {
    try {
      const { data, error } = await supabase
        .from('medecins')
        .select('*')
        .order('nom');

      if (error) return;
      
      if (data) {
        setMedecins(data);
      }
    } catch (error) {
      // Silencieux
    }
  };

  const loadToutesDisponibilites = async () => {
    try {
      const { data, error } = await supabase
        .from('disponibilites_medecins')
        .select(`
          *,
          medecins (
            nom,
            prenom,
            specialite
          )
        `)
        .order('jour_semaine');

      if (error) return;
      
      if (data) {
        setToutesDisponibilites(data);
      }
    } catch (error) {
      // Silencieux
    }
  };

  const loadRendezVous = async () => {
    try {
      const { data, error } = await supabase
        .from('rendez_vous')
        .select(`
          *,
          patients (
            id,
            nom,
            prenom,
            telephone,
            email
          ),
          medecins (
            id,
            nom,
            prenom,
            specialite
          )
        `)
        .order('date_rdv', { ascending: true });

      if (error) throw error;

      if (data) {
        const now = new Date();
        const rdvsToUpdate: string[] = [];

        data.forEach(rdv => {
          if (!rdv.date_rdv || !rdv.heure_fin) {
            return;
          }

          if (rdv.statut === 'en_attente' || rdv.statut === 'confirme') {
            const [year, month, day] = rdv.date_rdv.split('-').map(Number);
            const [heureFin, minuteFin] = rdv.heure_fin.split(':').map(Number);
            
            const rdvEndDate = new Date(year, month - 1, day, heureFin, minuteFin);
            
            if (rdvEndDate < now) {
              rdvsToUpdate.push(rdv.id);
            }
          }
        });

        if (rdvsToUpdate.length > 0) {
          const { error: updateError } = await supabase
            .from('rendez_vous')
            .update({ statut: 'manque' })
            .in('id', rdvsToUpdate);

          if (updateError) {
            // Silencieux
          }

          // Recharger les données après mise à jour
          const { data: updatedData } = await supabase
            .from('rendez_vous')
            .select(`
              *,
              patients (
                id,
                nom,
                prenom,
                telephone,
                email
              ),
              medecins (
                id,
                nom,
                prenom,
                specialite
              )
            `)
            .order('date_rdv', { ascending: true });

          if (updatedData) {
            setRendezVous(updatedData);
          }
        } else {
          setRendezVous(data);
        }
      }
    } catch (error) {
      // Silencieux
    }
  };

  const loadPatients = async () => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('nom');

      if (error) return;
      
      if (data) {
        setPatients(data);
      }
    } catch (error) {
      // Silencieux
    }
  };

  const loadDisponibilites = async (medecinId: string) => {
    try {
      const { data, error } = await supabase
        .from('disponibilites_medecins')
        .select('*')
        .eq('medecin_id', medecinId)
        .order('jour_semaine');

      if (error) return;
      
      if (data) {
        setDisponibilites(data);
      }
    } catch (error) {
      // Silencieux
    }
  };

  const handleSelectMedecin = async (medecin: Medecin) => {
    setSelectedMedecin(medecin);
    await loadDisponibilites(medecin.id);
    setActiveTab('disponibilites');
  };

  const loadAgendaData = async () => {
    setAgendaLoading(true);
    try {
      const dateObj = new Date(agendaDate + 'T00:00:00');
      const jourSemaine = dateObj.getDay();

      const medecinsToShow = agendaMedecinFilter
        ? medecins.filter(m => m.id === agendaMedecinFilter)
        : medecins;

      const agendaResults: AgendaMedecin[] = [];

      for (const medecin of medecinsToShow) {
        const disposDuJour = toutesDisponibilites.filter(
          d => d.medecin_id === medecin.id && d.jour_semaine === jourSemaine && d.actif
        );

        if (disposDuJour.length === 0) continue;

        const rdvsDuJour = rendezVous.filter(
          r => r.medecin_id === medecin.id && r.date_rdv === agendaDate && r.statut !== 'annule'
        );

        const slots: AgendaSlot[] = [];

        for (const dispo of disposDuJour) {
          const debutH = parseInt(dispo.heure_debut.split(':')[0]);
          const debutM = parseInt(dispo.heure_debut.split(':')[1]);
          const finH = parseInt(dispo.heure_fin.split(':')[0]);
          const finM = parseInt(dispo.heure_fin.split(':')[1]);

          const debutMinutes = debutH * 60 + debutM;
          const finMinutes = finH * 60 + finM;

          let currentMinutes = debutMinutes;
          while (currentMinutes + 30 <= finMinutes) {
            const slotDebut = `${String(Math.floor(currentMinutes / 60)).padStart(2, '0')}:${String(currentMinutes % 60).padStart(2, '0')}`;
            const slotFin = `${String(Math.floor((currentMinutes + 30) / 60)).padStart(2, '0')}:${String((currentMinutes + 30) % 60).padStart(2, '0')}`;

            const rdvQuiChevauche = rdvsDuJour.find(r => {
              return r.heure_debut < slotFin && r.heure_fin > slotDebut;
            });

            let rdvInfo: string | null = null;
            if (rdvQuiChevauche) {
              const patientNom = rdvQuiChevauche.patients
                ? `${rdvQuiChevauche.patients.prenom} ${rdvQuiChevauche.patients.nom}`
                : 'Patient';
              rdvInfo = `${patientNom} (${rdvQuiChevauche.heure_debut}-${rdvQuiChevauche.heure_fin})`;
            }

            slots.push({
              heure_debut: slotDebut,
              heure_fin: slotFin,
              est_occupe: !!rdvQuiChevauche,
              rdv_info: rdvInfo
            });

            currentMinutes += 30;
          }
        }

        if (slots.length > 0) {
          agendaResults.push({
            medecin,
            disponibilites: disposDuJour,
            slots
          });
        }
      }

      setAgendaData(agendaResults);
    } catch (error) {
      // Silencieux
    } finally {
      setAgendaLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getJourSemaineName = (jour: string) => {
    const jours: { [key: string]: string } = {
      'lundi': 'Lundi',
      'mardi': 'Mardi',
      'mercredi': 'Mercredi',
      'jeudi': 'Jeudi',
      'vendredi': 'Vendredi',
      'samedi': 'Samedi',
      'dimanche': 'Dimanche'
    };
    return jours[jour] || jour;
  };

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'bg-yellow-50 text-yellow-600';
      case 'confirme': return 'bg-blue-50 text-blue-600';
      case 'termine': return 'bg-green-50 text-green-600';
      case 'annule': return 'bg-red-50 text-red-600';
      case 'manque': return 'bg-gray-50 text-gray-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getStatusText = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'En attente';
      case 'confirme': return 'Confirmé';
      case 'termine': return 'Terminé';
      case 'annule': return 'Annulé';
      case 'manque': return 'Manqué';
      default: return statut;
    }
  };

  const getStatutBadge = (rdv: any) => {
    if (!rdv.date_rdv || !rdv.heure_debut || !rdv.heure_fin) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Données incomplètes
        </span>
      );
    }

    // Vérifier si le rendez-vous est passé
    const now = new Date();
    const [year, month, day] = rdv.date_rdv.split('-').map(Number);
    const [heureDebut, minuteDebut] = rdv.heure_debut.split(':').map(Number);
    const [heureFin, minuteFin] = rdv.heure_fin.split(':').map(Number);
    
    const rdvDateDebut = new Date(year, month - 1, day, heureDebut, minuteDebut);
    const rdvDateFin = new Date(year, month - 1, day, heureFin, minuteFin);
    
    const isPast = now > rdvDateFin;
    
    // Si le rendez-vous est passé et n'est pas terminé ou annulé, l'afficher comme manqué
    if (isPast && rdv.statut !== 'termine' && rdv.statut !== 'annule') {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Manqué
        </span>
      );
    }

    // Sinon afficher le statut normal
    switch (rdv.statut) {
      case 'en_attente':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            En attente
          </span>
        );
      case 'confirme':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            Confirmé
          </span>
        );
      case 'annule':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            Annulé
          </span>
        );
      case 'termine':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            Terminé
          </span>
        );
      default:
        return null;
    }
  };

  const formatDateNaissance = (dateString: string) => {
    if (!dateString) return 'Non renseignée';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateAge = (dateNaissance: string) => {
    if (!dateNaissance) return null;
    const today = new Date();
    const birthDate = new Date(dateNaissance + 'T00:00:00');
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const filteredMedecins = medecins.filter(m =>
    `${m.nom} ${m.prenom} ${m.specialite}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRendezVous = rendezVous.filter(rdv => {
    const patientNom = rdv.patients?.nom || 'Patient inconnu';
    const patientPrenom = rdv.patients?.prenom || '';
    const medecinNom = rdv.medecins?.nom || 'Médecin inconnu';
    const medecinPrenom = rdv.medecins?.prenom || '';
    
    return `${patientNom} ${patientPrenom} ${medecinNom} ${medecinPrenom}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
  });

  const getRendezVousStats = () => {
    const now = new Date();
    
    const aVenir = rendezVous.filter(rdv => {
      if (!rdv.date_rdv || !rdv.heure_debut) {
        return false;
      }

      const [year, month, day] = rdv.date_rdv.split('-').map(Number);
      const [heureDebut, minuteDebut] = rdv.heure_debut.split(':').map(Number);
      const rdvDate = new Date(year, month - 1, day, heureDebut, minuteDebut);
      const isAVenir = rdvDate >= now && ['en_attente', 'confirme'].includes(rdv.statut);
      return isAVenir;
    });
    
    const manques = rendezVous.filter(rdv => rdv.statut === 'manque');
    const passes = rendezVous.filter(rdv => ['termine', 'annule'].includes(rdv.statut));
    const urgents = rendezVous.filter(rdv => rdv.type_rdv === 'urgent' && ['en_attente', 'confirme'].includes(rdv.statut));

    return { aVenir, manques, passes, urgents };
  };

  const stats = getRendezVousStats();

  const handleAnnulerRdv = async (rdv: RendezVous) => {
    if (!confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) return;

    try {
      await offlineMutate({
        table: 'rendez_vous',
        operation: 'UPDATE',
        recordId: rdv.id,
        data: { statut: 'annule', date_modification: new Date().toISOString() },
        metadata: { summary: `Annulation RDV — ${rdv.patients?.nom || 'Patient'}` },
        onlineExecutor: async () => {
          const { error } = await supabase
            .from('rendez_vous')
            .update({ statut: 'annule', date_modification: new Date().toISOString() })
            .eq('id', rdv.id);
          if (error) throw error;
          return { id: rdv.id };
        },
      });
      await loadRendezVous();
    } catch (error: any) {
      console.error('Erreur annulation:', error);
      alert('Erreur lors de l\'annulation du rendez-vous');
    }
  };

  const handleModifierRdv = (rdv: RendezVous) => {
    setSelectedRdvToEdit(rdv);
    setShowModifierModal(true);
  };

  // Afficher le loader local si nécessaire
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement des données...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-clinic-blue/5 via-white to-clinic-green/5">
      <Header />

      <div className="bg-gradient-to-br from-clinic-blue-dark via-clinic-blue to-clinic-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold mb-4">
                <i className="ri-calendar-check-line"></i>
                Espace Secrétaire
              </div>
              <h1 className="text-2xl md:text-4xl font-bold mb-2">Tableau de bord Secrétaire</h1>
              <p className="text-white/90 text-sm md:text-base">Gérez les rendez-vous des médecins de la clinique</p>
            </div>
            <div className="flex flex-wrap gap-2 md:gap-3">
              <button
                onClick={() => navigate('/secretaire/gestion-patients')}
                className="px-4 md:px-6 py-2.5 md:py-3 bg-white text-clinic-blue-dark rounded-full hover:bg-white/95 hover:shadow-lg transition-all font-semibold whitespace-nowrap cursor-pointer text-xs md:text-sm flex items-center gap-1.5"
              >
                <i className="ri-user-heart-line"></i>
                Patients
              </button>
              <button
                onClick={() => navigate('/file-attente')}
                className="px-4 md:px-6 py-2.5 md:py-3 bg-white/10 text-white border border-white/40 rounded-full backdrop-blur-sm hover:bg-white/20 transition-all font-semibold whitespace-nowrap cursor-pointer text-xs md:text-sm flex items-center gap-1.5"
              >
                <i className="ri-time-line"></i>
                File d'attente
              </button>
              <button
                onClick={() => navigate('/secretaire/nouveau-rendez-vous')}
                className="px-4 md:px-6 py-2.5 md:py-3 bg-clinic-green text-white rounded-full ring-2 ring-white/30 hover:bg-clinic-green-dark hover:shadow-lg transition-all font-semibold whitespace-nowrap cursor-pointer text-xs md:text-sm flex items-center gap-1.5"
              >
                <i className="ri-add-line"></i>
                Nouveau RDV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 py-6 md:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* ✅ Statistiques des rendez-vous */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="bg-white p-4 md:p-5 rounded-2xl border border-clinic-blue/10 shadow-sm flex items-center gap-3 md:gap-4">
              <div className="w-11 h-11 md:w-14 md:h-14 bg-clinic-blue/10 rounded-2xl flex items-center justify-center shrink-0">
                <i className="ri-calendar-line text-xl md:text-2xl text-clinic-blue"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-0.5">À venir</p>
                <p className="text-2xl md:text-3xl font-bold text-clinic-blue-dark">{stats.aVenir.length}</p>
              </div>
            </div>

            <div className="bg-white p-4 md:p-5 rounded-2xl border border-red-100 shadow-sm flex items-center gap-3 md:gap-4">
              <div className="w-11 h-11 md:w-14 md:h-14 bg-red-50 rounded-2xl flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-xl md:text-2xl text-red-500"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-0.5">Urgents</p>
                <p className="text-2xl md:text-3xl font-bold text-red-600">{stats.urgents.length}</p>
              </div>
            </div>

            <div className="bg-white p-4 md:p-5 rounded-2xl border border-amber-100 shadow-sm flex items-center gap-3 md:gap-4">
              <div className="w-11 h-11 md:w-14 md:h-14 bg-amber-50 rounded-2xl flex items-center justify-center shrink-0">
                <i className="ri-close-circle-line text-xl md:text-2xl text-amber-500"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-0.5">Manqués</p>
                <p className="text-2xl md:text-3xl font-bold text-amber-600">{stats.manques.length}</p>
              </div>
            </div>

            <div className="bg-white p-4 md:p-5 rounded-2xl border border-clinic-green/20 shadow-sm flex items-center gap-3 md:gap-4">
              <div className="w-11 h-11 md:w-14 md:h-14 bg-clinic-green/10 rounded-2xl flex items-center justify-center shrink-0">
                <i className="ri-check-double-line text-xl md:text-2xl text-clinic-green"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm text-gray-500 mb-0.5">Passés</p>
                <p className="text-2xl md:text-3xl font-bold text-clinic-green-dark">{stats.passes.length}</p>
              </div>
            </div>
          </div>

          {/* Onglets */}
          <div className="mb-6 md:mb-8 overflow-x-auto">
            <div className="flex gap-1.5 bg-white p-1.5 rounded-full border border-clinic-blue/10 min-w-max shadow-sm">
              <button
                onClick={() => setActiveTab('medecins')}
                className={`flex-1 min-w-[90px] px-4 py-2.5 md:py-3 rounded-full font-semibold text-xs md:text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'medecins'
                    ? 'bg-gradient-to-r from-clinic-blue to-clinic-blue-dark text-white shadow-md'
                    : 'text-gray-500 hover:bg-clinic-blue/5 hover:text-clinic-blue-dark'
                }`}
              >
                Médecins
              </button>
              <button
                onClick={() => setActiveTab('rendez-vous')}
                className={`flex-1 min-w-[90px] px-4 py-2.5 md:py-3 rounded-full font-semibold text-xs md:text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'rendez-vous'
                    ? 'bg-gradient-to-r from-clinic-blue to-clinic-blue-dark text-white shadow-md'
                    : 'text-gray-500 hover:bg-clinic-blue/5 hover:text-clinic-blue-dark'
                }`}
              >
                RDV
              </button>
              <button
                onClick={() => {
                  setActiveTab('disponibilites');
                  setSelectedMedecin(null);
                }}
                className={`flex-1 min-w-[90px] px-4 py-2.5 md:py-3 rounded-full font-semibold text-xs md:text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'disponibilites'
                    ? 'bg-gradient-to-r from-clinic-blue to-clinic-blue-dark text-white shadow-md'
                    : 'text-gray-500 hover:bg-clinic-blue/5 hover:text-clinic-blue-dark'
                }`}
              >
                Disponibilités
              </button>
              <button
                onClick={() => setActiveTab('agenda')}
                className={`flex-1 min-w-[90px] px-4 py-2.5 md:py-3 rounded-full font-semibold text-xs md:text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'agenda'
                    ? 'bg-gradient-to-r from-clinic-blue to-clinic-blue-dark text-white shadow-md'
                    : 'text-gray-500 hover:bg-clinic-blue/5 hover:text-clinic-blue-dark'
                }`}
              >
                Agenda
              </button>
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="mb-6">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  activeTab === 'medecins' 
                    ? 'Rechercher un médecin...' 
                    : activeTab === 'rendez-vous'
                    ? 'Rechercher un rendez-vous...'
                    : activeTab === 'agenda'
                    ? 'Rechercher dans l\'agenda...'
                    : 'Rechercher une disponibilité...'
                }
                className="w-full pl-12 pr-4 py-4 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
              />
            </div>
          </div>

          {/* Contenu des onglets */}
          {activeTab === 'medecins' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMedecins.map((medecin) => (
                <div
                  key={medecin.id}
                  className="bg-white p-5 md:p-6 rounded-xl border border-gray-100 hover:border-clinic-blue/30 transition-all cursor-pointer"
                  onClick={() => handleSelectMedecin(medecin)}
                >
                  <div className="min-w-0 overflow-hidden">
                    <h3 className="text-lg md:text-xl font-bold text-clinic-blue-dark mb-1 truncate">
                      Dr. {medecin.nom} {medecin.prenom}
                    </h3>
                    <p className="text-clinic-blue-dark/70 font-medium mb-2 md:mb-3 truncate text-sm md:text-base">{medecin.specialite}</p>
                    <div className="space-y-1.5 text-sm text-gray-500">
                      <span className="block break-all leading-tight">{medecin.email}</span>
                      <span className="block break-all leading-tight">{medecin.telephone}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-sm font-medium text-clinic-blue-dark">Voir les disponibilités</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'rendez-vous' && (
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              {filteredRendezVous.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <i className="ri-calendar-line text-4xl text-clinic-blue"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-clinic-blue-dark mb-2">Aucun rendez-vous</h3>
                  <p className="text-gray-600">Aucun rendez-vous trouvé</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredRendezVous.map((rdv) => (
                    <div
                      key={rdv.id}
                      className="p-4 md:p-6 rounded-xl border border-gray-100 hover:border-clinic-blue/30 transition-all"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="mb-3">
                            <p className="text-base md:text-lg font-bold text-clinic-blue-dark truncate">
                              {rdv.date_rdv ? formatDate(rdv.date_rdv) : 'Date non définie'}
                            </p>
                            <p className="text-clinic-blue-dark/70 font-semibold text-sm md:text-base">
                              {rdv.heure_debut || '??:??'} - {rdv.heure_fin || '??:??'}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <p className="text-gray-700 text-sm break-words">
                              <strong className="whitespace-nowrap">Patient :</strong> {rdv.patients ? `${rdv.patients.nom} ${rdv.patients.prenom}` : 'Patient inconnu'}
                            </p>
                            <p className="text-gray-700 text-sm break-words">
                              <strong className="whitespace-nowrap">Médecin :</strong> {rdv.medecins ? `Dr. ${rdv.medecins.nom} ${rdv.medecins.prenom} (${rdv.medecins.specialite})` : 'Médecin inconnu'}
                            </p>
                            {rdv.patients?.telephone && (
                              <p className="text-gray-700 text-sm break-all">{rdv.patients.telephone}</p>
                            )}
                            {rdv.patients?.email && (
                              <p className="text-gray-700 text-sm break-all">{rdv.patients.email}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex-shrink-0 self-start flex items-center gap-2">
                          {rdv.type_rdv === 'urgent' && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200 whitespace-nowrap">
                              URGENT
                            </span>
                          )}
                          {getStatutBadge(rdv)}
                        </div>
                      </div>

                      {(rdv.statut === 'en_attente' || rdv.statut === 'confirme') && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModifierRdv(rdv);
                            }}
                            className="px-4 py-2 border border-clinic-blue/30 text-clinic-blue-dark rounded-lg text-sm font-medium hover:bg-clinic-blue/5 transition-all whitespace-nowrap cursor-pointer"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAnnulerRdv(rdv);
                            }}
                            className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-all whitespace-nowrap cursor-pointer"
                          >
                            Annuler
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'disponibilites' && (
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              {selectedMedecin ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-clinic-blue-dark">
                        Disponibilités de Dr. {selectedMedecin.nom} {selectedMedecin.prenom}
                      </h2>
                      <p className="text-gray-600 mt-1">{selectedMedecin.specialite}</p>
                    </div>
                  </div>

                  {disponibilites.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <i className="ri-time-line text-4xl text-clinic-blue"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-clinic-blue-dark mb-2">Aucune disponibilité</h3>
                      <p className="text-gray-600">Ce médecin n'a pas encore configuré ses disponibilités</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {disponibilites.map((disp) => (
                        <div
                          key={disp.id}
                          className={`p-4 rounded-xl border-2 ${
                            disp.actif
                              ? 'bg-clinic-green/5 border-clinic-green/30'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-clinic-blue/10 rounded-xl flex items-center justify-center">
                                <i className="ri-calendar-line text-clinic-blue"></i>
                              </div>
                              <div>
                                <div className="font-semibold text-clinic-blue-dark">
                                  {JOURS_SEMAINE[disp.jour_semaine]}
                                </div>
                                <div className="text-sm text-gray-600 flex items-center gap-2">
                                  <i className="ri-time-line text-clinic-green"></i>
                                  {disp.heure_debut} - {disp.heure_fin}
                                </div>
                              </div>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              disp.actif
                                ? 'bg-clinic-green text-white'
                                : 'bg-gray-300 text-gray-600'
                            }`}>
                              {disp.actif ? 'Disponible' : 'Indisponible'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-clinic-blue-dark">
                      Toutes les disponibilités
                    </h2>
                    <p className="text-gray-600 mt-1">Disponibilités de tous les médecins</p>
                  </div>

                  {toutesDisponibilites.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <i className="ri-time-line text-4xl text-clinic-blue"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-clinic-blue-dark mb-2">Aucune disponibilité</h3>
                      <p className="text-gray-600">Aucun médecin n'a encore configuré ses disponibilités</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {medecins.map((medecin) => {
                        const disposMedecin = toutesDisponibilites.filter(d => d.medecin_id === medecin.id);
                        
                        if (disposMedecin.length === 0) return null;

                        return (
                          <div key={medecin.id} className="border-2 border-clinic-blue/10 rounded-2xl p-6">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-clinic-blue rounded-xl flex items-center justify-center">
                                  <i className="ri-stethoscope-line text-xl text-white"></i>
                                </div>
                                <div>
                                  <h3 className="text-lg font-bold text-clinic-blue-dark">
                                    Dr. {medecin.nom} {medecin.prenom}
                                  </h3>
                                  <p className="text-sm text-gray-600">{medecin.specialite}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleSelectMedecin(medecin)}
                                className="px-4 py-2 bg-clinic-blue text-white rounded-full text-sm font-medium hover:bg-clinic-blue-dark transition-all whitespace-nowrap cursor-pointer"
                              >
                                Voir détails
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {disposMedecin.map((dispo) => (
                                <div
                                  key={dispo.id}
                                  className={`p-4 rounded-xl border transition-all ${
                                    dispo.actif
                                      ? 'bg-green-50 border-green-200'
                                      : 'bg-gray-50 border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                        dispo.actif ? 'bg-green-500' : 'bg-gray-400'
                                      }`}>
                                        <i className="ri-calendar-line text-lg text-white"></i>
                                      </div>
                                      <div>
                                        <p className="font-bold text-gray-900">
                                          {JOURS_SEMAINE[dispo.jour_semaine]}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          {dispo.heure_debut} - {dispo.heure_fin}
                                        </p>
                                      </div>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                      dispo.actif
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-200 text-gray-700'
                                    }`}>
                                      {dispo.actif ? 'Dispo' : 'Indispo'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'agenda' && (
            <CalendarWeekView
              rendezVous={rendezVous}
              medecins={medecins}
              onModify={handleModifierRdv}
            />
          )}
        </div>
      </div>

      <Footer />

      <ModifierRendezVousModal
        isOpen={showModifierModal}
        rendezVous={selectedRdvToEdit}
        onClose={() => {
          setShowModifierModal(false);
          setSelectedRdvToEdit(null);
        }}
        onSuccess={loadRendezVous}
      />
    </div>
  );
}
